<template>
  <basic-modal.container
    id="productReplacementModal"
    ref="modalRef"
  >
    <basic-modal.step
      :id="`dialog_${fromProduct?.productId}_${toProduct?.productId}`"
      type="transactional"
      :step-title="$t('product.replace.title')"
    >
      <basic-modal.content>
        <div class="replace">
          <div class="replace__product">
            <div
              class="title-2"
              v-text="$t('product.replace.selected')"
            />

            <the-product-tile
              v-if="fromProduct"
              options="read"
              :hidden-items="['actions', 'altDescription']"
              :category-id="getCategoryIdFromProduct(fromProduct.productId)"
              :group-id="getGroupIdFromProduct(fromProduct.productId)"
              :person="person"
              :person-id="person.personId"
              :product="fromProduct"
              :product-id="fromProduct.productId"
              :product-state="state(person.partnerNumber, fromProduct.productId)"
            />
          </div>

          <div class="replace__product replace__product--selected">
            <div
              class="title-2"
              v-text="$t('product.replace.selectable')"
            />

            <the-product-tile
              v-if="toProduct"
              options="read"
              :hidden-items="['actions', 'altDescription']"
              :category-id="getCategoryIdFromProduct(toProduct.productId)"
              :group-id="getGroupIdFromProduct(toProduct.productId)"
              :person="person"
              :person-id="person.personId"
              :product="toProduct"
              :product-id="toProduct.productId"
              :product-state="state(person.partnerNumber, toProduct.productId)"
            />
          </div>
        </div>
      </basic-modal.content>

      <basic-modal.footer
        :primary-label="$t('product.replace.confirm')"
        @click-primary="confirm"
        @click-secondary="cancel"
      />
    </basic-modal.step>
  </basic-modal.container>
</template>

<script setup>
  import { nextTick, onMounted, ref } from 'vue'
  import { cloneDeep } from 'lodash'

  import { events$, loading$ } from '@shared/services'
  import { ReactivityUtil } from '@shared/utils/Reactivity'

  import basketStore from '@shared/store/basket'
  import usePersonSpecificProduct from '@shared/hooks/usePersonSpecificProduct'
  import useProduct from '@shared/hooks/useProduct'
  import useProductPersons from '@shared/hooks/useProductPersons'
  import useProductState from '@shared/hooks/useProductState'

  import BasicModal from '@shared/components/Basic/Modal/index.js'
  import TheProductTile from '@shared/components/Product/TheProductTile'

  import { EVENT_PRODUCT } from '@shared/config/events'

  // HOOKS
  const { getCategoryIdFromProduct, getGroupIdFromProduct, isUpgrade } = useProduct()
  const { changeProductOption, getProduct, updateProductForPerson } = usePersonSpecificProduct()
  const { addProductToOvpBasket } = useProductPersons()
  const { state } = useProductState()

  // DATA
  const modalRef = ref()
  const person = ref(null)
  const fromProduct = ref(null)
  const source = ref(null)
  const toProduct = ref(null)
  const callback = ref(null)

  // METHODS
  function cancel() {
    modalRef.value.close()

    person.value = null
    fromProduct.value = null
    toProduct.value = null
    source.value = null
  }

  async function confirm() {
    loading$.start({ blocking: false })

    const isUpdate = fromProduct.value.productId === toProduct.value.productId

    try {
      const mode = isUpgrade(toProduct.value.productId) ? 'ADD' : 'REPLACE'
      let basket = updateProductForPerson({ personId: person.value.personId, product: toProduct.value })
      basket.persons.find(p => p.personId === person.value.personId).products.products[
        fromProduct.value.productId
      ].selected = false
      basket = await addProductToOvpBasket(person.value.personId, toProduct.value.productId, { basket, mode })
      basket = await basketStore.updateOvpBasket(basket)

      if (!isUpdate) {
        events$.emit(EVENT_PRODUCT.REMOVED, {
          basket,
          categoryId: getCategoryIdFromProduct(fromProduct.value.productId),
          personId: person.value.personId,
          productId: fromProduct.value.productId,
          source: source.value,
          silent: true,
        })
      }

      events$.emit(isUpdate ? EVENT_PRODUCT.CHANGED : EVENT_PRODUCT.ADDED, {
        basket,
        categoryId: getCategoryIdFromProduct(toProduct.value.productId),
        personId: person.value.personId,
        productId: toProduct.value.productId,
        source: source.value,
      })

      loading$.end()

      if (callback.value) callback.value()

      cancel()
    } catch {
      loading$.failed()
    }
  }

  async function openModal(data) {
    loading$.start()

    try {
      const basket = cloneDeep(basketStore.basket)
      basket.persons.find(p => p.personId === data.person.personId).products.products[data.fromProductId].selected = false

      callback.value = data.callback ? data.callback : null
      person.value = data.person
      fromProduct.value = Object.assign({}, getProduct(data.person.personId, data.fromProductId), {
        productId: data.fromProductId,
      })
      source.value = data.source

      if (data.simulate) {
        toProduct.value = await changeProductOption({
          option: fromProduct.value.prices.find(p => p.selected),
          personId: data.person.personId,
          product: Object.assign({}, ReactivityUtil.clone(data.toProduct), { selected: true }),
          basket,
          params: {
            personId: data.person.personId,
            selectedCalculations: true,
          },
        })
      } else {
        toProduct.value = Object.assign({}, data.toProduct, { selected: true })
      }

      await nextTick()

      modalRef.value.open()

      loading$.end()

      events$.emit(EVENT_PRODUCT.REPLACE_DISPLAYED)
    } catch {
      loading$.failed()
    }
  }

  // LIFECYCLE EVENTS
  onMounted(() => {
    events$.on(EVENT_PRODUCT.REPLACE_OPEN, data => openModal(data))
  })
</script>

<style scoped>
.replace {
  display: flex;
  flex-direction: column;
  margin: var(--spacing-san-spacing-06) 0 var(--spacing-san-spacing-04) 0;

  .title-2 {
    margin: 0 0 var(--spacing-san-spacing-04) 0;
  }

  .replace__product {
    display: grid;
    column-gap: var(--spacing-san-spacing-04);
    grid-template-rows: repeat(9, minmax(0, min-content));
    margin: 0 0 var(--spacing-san-spacing-04) 0;

    &:not(.replace__product--selected) {
      .product--selected {
        border: 1px solid var(--outline-variant);
        background-color: transparent;
        box-shadow: none;
      }
    }

    .product {
      grid-row: 2 / 9;
      margin: 0;
    }
  }
}

@media (--v-large) {
  .replace {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    grid-template-rows: repeat(9, minmax(0, min-content));
    column-gap: var(--spacing-san-spacing-06);

    .replace__product {
      display: grid;
      grid-row: 1 / -1;
      grid-template-columns: 1fr;
      grid-template-rows: subgrid;
    }
  }
}
</style>
